import React, { useCallback, useEffect, useState } from 'react';
import { useLessonContext } from '../../LessonContext';
import { courseRunsService } from '../../../../services/courseRunsService';

export function LessonReview() {
  const { document, lessonRun, refreshDocument, refreshRun } = useLessonContext();
  const lessonContent: any = document?.lesson_content;
  const isApproved = (lessonRun?.stages || []).some(
    (s) => s.stage_id === 'lesson_review' && s.status === 'succeeded'
  );

  const [draft, setDraft] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);
  const [approving, setApproving] = useState(false);
  const [approveError, setApproveError] = useState<string | null>(null);

  useEffect(() => {
    if (lessonContent) {
      setDraft(JSON.parse(JSON.stringify(lessonContent)));
    }
  }, [lessonContent]);

  const isDirty = draft && lessonContent ? JSON.stringify(draft) !== JSON.stringify(lessonContent) : false;

  const handleSave = useCallback(async () => {
    if (!lessonRun?.run_id || !draft) return;
    setSaving(true);
    setSaveError(null);
    setSavedMessage(null);
    try {
      await courseRunsService.updateLessonDocument(lessonRun.run_id, { lesson_content: draft });
      await refreshDocument();
      setSavedMessage('Saved');
      setTimeout(() => setSavedMessage(null), 3000);
    } catch (e: unknown) {
      setSaveError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }, [lessonRun?.run_id, draft, refreshDocument]);

  const handleDiscard = () => {
    if (lessonContent) setDraft(JSON.parse(JSON.stringify(lessonContent)));
    setSaveError(null);
    setSavedMessage(null);
  };

  const handleApprove = useCallback(async () => {
    if (!lessonRun?.run_id) return;
    setApproving(true);
    setApproveError(null);
    try {
      await courseRunsService.approveLessonStage(lessonRun.run_id, 'lesson_review');
      await refreshRun();
    } catch (e: unknown) {
      setApproveError(e instanceof Error ? e.message : 'Failed to approve');
    } finally {
      setApproving(false);
    }
  }, [lessonRun?.run_id, refreshRun]);

  if (!lessonContent) {
    return (
      <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
        Generate the lesson first before reviewing.
      </div>
    );
  }

  if (!draft) return null;

  const updateSection = (sIdx: number, updated: any) => {
    const sections = [...draft.sections];
    sections[sIdx] = updated;
    setDraft({ ...draft, sections });
  };

  const updateContentItem = (sIdx: number, cIdx: number, updated: any) => {
    const sections = [...draft.sections];
    const content = [...sections[sIdx].content];
    content[cIdx] = updated;
    sections[sIdx] = { ...sections[sIdx], content };
    setDraft({ ...draft, sections });
  };

  return (
    <div className="space-y-6 pb-16">
      {/* Sticky header */}
      <div className="sticky top-0 z-20 -mx-4 px-4 py-3 bg-white/90 backdrop-blur-sm border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Lesson Review</h3>
            <p className="text-xs text-gray-500">Edit text, adjust exercise specs, then approve</p>
          </div>
          <div className="flex items-center gap-3">
            {savedMessage && <span className="text-xs text-green-600">{savedMessage}</span>}
            {saveError && <span className="text-xs text-red-600">{saveError}</span>}
            <button type="button" className="px-3 py-1.5 text-sm border rounded-md border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40" disabled={!isDirty} onClick={handleDiscard}>Discard</button>
            <button type="button" className="px-4 py-1.5 text-sm rounded-md text-white hover:opacity-90 disabled:opacity-40" style={{ backgroundColor: '#1a2332' }} disabled={saving || !isDirty} onClick={handleSave}>{saving ? 'Saving...' : 'Save Changes'}</button>
            {!isApproved && !isDirty && (
              <button type="button" className="px-4 py-1.5 text-sm rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-40" disabled={approving || isDirty} onClick={handleApprove}>{approving ? 'Approving...' : 'Approve Lesson'}</button>
            )}
          </div>
        </div>
      </div>

      {isApproved && (
        <div className="bg-green-50 border border-green-200 rounded-md px-4 py-3 text-sm text-green-800 flex items-center gap-2">
          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          Lesson approved. Proceed to Exercise Generation.
        </div>
      )}

      {approveError && (
        <div className="bg-red-50 border border-red-200 rounded-md px-4 py-3 text-sm text-red-700">{approveError}</div>
      )}

      {/* Sections */}
      {(draft.sections || []).map((section: any, sIdx: number) => (
        <div key={section.section_id || sIdx} className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
            <input
              className="text-sm font-medium text-gray-900 bg-transparent border-0 border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none w-full"
              value={section.title || ''}
              placeholder="Section title"
              onChange={(e) => updateSection(sIdx, { ...section, title: e.target.value })}
            />
          </div>
          <div className="p-4 space-y-3">
            {(section.content || []).map((item: any, cIdx: number) => (
              <EditableContentItem
                key={cIdx}
                item={item}
                onChange={(updated) => updateContentItem(sIdx, cIdx, updated)}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function EditableContentItem({ item, onChange }: { item: any; onChange: (updated: any) => void }) {
  if (item.type === 'text') {
    return (
      <textarea
        className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm leading-relaxed focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-y"
        rows={6}
        value={item.body || ''}
        onChange={(e) => onChange({ ...item, body: e.target.value })}
        onInput={(e) => {
          const el = e.target as HTMLTextAreaElement;
          el.style.height = 'auto';
          el.style.height = el.scrollHeight + 'px';
        }}
      />
    );
  }

  if (item.type === 'exercise') {
    return (
      <div className="bg-green-50 border border-green-200 rounded-md p-3 space-y-2">
        <div className="text-[10px] font-bold uppercase text-green-600">Exercise</div>
        <textarea
          className="w-full border border-green-200 rounded px-2 py-1.5 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-green-400 resize-y"
          rows={3}
          value={item.intent || ''}
          placeholder="What this exercise should test..."
          onChange={(e) => onChange({ ...item, intent: e.target.value })}
        />
      </div>
    );
  }

  if (item.type === 'comparison') {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-md p-3 space-y-2">
        <div className="text-[10px] font-bold uppercase text-amber-600">Comparison</div>
        <div className="grid grid-cols-2 gap-2">
          {(item.items || []).map((comp: any, i: number) => (
            <div key={i} className="bg-white border border-gray-200 rounded p-2 space-y-1">
              <input className="w-full border-0 border-b border-gray-200 focus:border-blue-500 focus:outline-none text-xs font-medium" value={comp.label || ''} onChange={(e) => {
                const items = [...item.items]; items[i] = { ...items[i], label: e.target.value }; onChange({ ...item, items });
              }} />
              <textarea className="w-full border border-gray-200 rounded px-2 py-1 text-xs resize-y" rows={2} value={comp.content || ''} onChange={(e) => {
                const items = [...item.items]; items[i] = { ...items[i], content: e.target.value }; onChange({ ...item, items });
              }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (item.type === 'callout') {
    return (
      <div className="bg-cyan-50 border border-cyan-200 rounded-md p-3">
        <div className="text-[10px] font-bold uppercase text-cyan-600 mb-1">Callout</div>
        <textarea className="w-full border border-cyan-200 rounded px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-cyan-400 resize-y" rows={2} value={item.body || ''} onChange={(e) => onChange({ ...item, body: e.target.value })} />
      </div>
    );
  }

  if (item.type === 'image') {
    return (
      <div className="bg-purple-50 border border-purple-200 rounded-md p-3 space-y-1">
        <div className="text-[10px] font-bold uppercase text-purple-600">Image</div>
        <textarea className="w-full border border-purple-200 rounded px-2 py-1.5 text-xs bg-white resize-y" rows={2} value={item.description || ''} placeholder="Image description..." onChange={(e) => onChange({ ...item, description: e.target.value })} />
      </div>
    );
  }

  if (item.type === 'audio') {
    return (
      <div className="bg-pink-50 border border-pink-200 rounded-md p-3 space-y-1">
        <div className="text-[10px] font-bold uppercase text-pink-600">Audio</div>
        <textarea className="w-full border border-pink-200 rounded px-2 py-1.5 text-xs bg-white resize-y" rows={2} value={item.description || ''} placeholder="Audio description..." onChange={(e) => onChange({ ...item, description: e.target.value })} />
      </div>
    );
  }

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
      <div className="text-[10px] font-bold uppercase text-gray-400 mb-1">{item.type}</div>
      <pre className="text-xs text-gray-600">{JSON.stringify(item, null, 2)}</pre>
    </div>
  );
}
